/**
 * Adapter-ready monthly production CSV → lease-level history for the desk.
 * Same columns as asset-core production-csv: well_id, name, date, oil, gas, water.
 * RRC-<district>-<lease> ids (or a "(lease)" name) are leases. The public Texas
 * pull is labeled "Texas RRC (public)". The CSV itself stays off git.
 */

export const RRC_PUBLIC_SOURCE = 'Texas RRC (public)';

const LEASE_ID = /^RRC-[0-9A-Za-z]+-\d+$/;
const LEASE_SUFFIX = /\s*\(lease\)\s*$/i;

export function parseCsv(text) {
  const rows = [];
  let row = [];
  let cur = '';
  let quoted = false;
  const s = String(text || '').replace(/^\uFEFF/, '');
  for (let i = 0; i < s.length; i += 1) {
    const c = s[i];
    if (quoted) {
      if (c === '"') {
        if (s[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else quoted = false;
      } else cur += c;
    } else if (c === '"') quoted = true;
    else if (c === ',') {
      row.push(cur);
      cur = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && s[i + 1] === '\n') i += 1;
      row.push(cur);
      cur = '';
      if (row.some((cell) => cell !== '')) rows.push(row);
      row = [];
    } else cur += c;
  }
  if (cur !== '' || row.length) {
    row.push(cur);
    if (row.some((cell) => cell !== '')) rows.push(row);
  }
  return rows;
}

function isLease(id, name) {
  return LEASE_ID.test(id) || LEASE_SUFFIX.test(name || '');
}

function num(v) {
  if (v == null || String(v).trim() === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * @returns {null | { grain: 'lease', source: string, leases: Array }}
 * Null when the file has no lease rows (well-level CSVs stay on the well path).
 */
export function parseLeaseProductionCsv(text, { sourceLabel } = {}) {
  const rows = parseCsv(text);
  if (rows.length < 2) return null;
  const header = rows[0].map((h) => h.trim().toLowerCase());
  const col = (name) => header.indexOf(name);
  const idCol = col('well_id') >= 0 ? col('well_id') : col('api');
  const nameCol = col('name');
  const dateCol = col('date');
  const oilCol = col('oil');
  const gasCol = col('gas');
  if (idCol < 0 || dateCol < 0) return null;
  const byId = new Map();
  let rrc = false;
  for (const cells of rows.slice(1)) {
    const id = (cells[idCol] || '').trim();
    const rawName = nameCol >= 0 ? (cells[nameCol] || '').trim() : '';
    if (!id || !isLease(id, rawName)) continue;
    if (LEASE_ID.test(id)) rrc = true;
    const month = (cells[dateCol] || '').trim().slice(0, 7);
    if (!/^\d{4}-\d{2}$/.test(month)) continue;
    let lease = byId.get(id);
    if (!lease) {
      lease = {
        id,
        name: rawName.replace(LEASE_SUFFIX, '').trim() || id,
        monthly: [],
      };
      byId.set(id, lease);
    }
    const point = { month };
    const oil = oilCol >= 0 ? num(cells[oilCol]) : null;
    const gas = gasCol >= 0 ? num(cells[gasCol]) : null;
    if (oil != null) point.oilBbl = oil;
    if (gas != null) point.gasMcf = gas;
    if (point.oilBbl == null && point.gasMcf == null) continue;
    const prev = lease.monthly.findIndex((p) => p.month === month);
    if (prev >= 0) lease.monthly[prev] = point;
    else lease.monthly.push(point);
  }
  if (!byId.size) return null;
  const leases = [...byId.values()]
    .map((lease) => ({ ...lease, monthly: lease.monthly.sort((a, b) => a.month.localeCompare(b.month)) }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const source = (sourceLabel || '').trim() || (rrc ? RRC_PUBLIC_SOURCE : null);
  return { grain: 'lease', source, leases };
}

/** First distinctive lease token. STALLINGS and STALLING share a stem. */
export function leaseMatchKey(name) {
  const tokens = String(name || '')
    .toUpperCase()
    .replace(/\(LEASE\)/g, ' ')
    .split(/[^A-Z0-9]+/)
    .filter((t) => t && !['LEASE', 'THE', 'SR', 'JR'].includes(t));
  if (!tokens.length) return '';
  let token = tokens[0];
  if (token.endsWith('S') && token.length > 5) token = token.slice(0, -1);
  return token;
}

/**
 * Attach each RRC lease to the oneline case with the same lease stem and copy
 * that case's seller forecast (gross oil / gas) onto the lease.
 */
export function joinLeaseHistory(pkg, history) {
  if (!history?.leases?.length) return history;
  const assets = pkg?.assets || [];
  const streams = new Map((pkg?.streams || []).map((s) => [s.propnum, s]));
  const used = new Set();
  for (const lease of history.leases) {
    const key = leaseMatchKey(lease.name || lease.id);
    const asset = assets.find((a) => !used.has(a.propnum) && leaseMatchKey(a.lease) === key);
    if (!asset) continue;
    used.add(asset.propnum);
    lease.propnum = asset.propnum;
    asset.rrcLeaseId = lease.id;
    const months = streams.get(asset.propnum)?.months || [];
    if (months.length) {
      lease.forecast = months.map((m) => ({
        month: String(m.date || '').slice(0, 7),
        oilBbl: m.grossOilBbl,
        netOilBbl: m.netOilBbl,
        gasMcf: m.grossMcf,
      })).filter((p) => /^\d{4}-\d{2}$/.test(p.month));
    }
  }
  return history;
}

/** Attach lease history onto a served deal package and clear the extract's missing flag. */
export function attachProductionHistory(pkg, history) {
  if (!pkg || !history?.leases?.length) return pkg;
  joinLeaseHistory(pkg, history);
  pkg.productionHistory = history;
  if (Array.isArray(pkg.meta?.missing)) {
    pkg.meta.missing = pkg.meta.missing.filter((m) => m !== 'productionHistory');
  }
  if (pkg.provenance && typeof pkg.provenance === 'object' && history.source) {
    pkg.provenance.productionHistory = `${history.source} · lease-level`;
  }
  return pkg;
}
